/**
 * A workflow has two flow codes: the one it runs as, and the one it is
 * backtested as, which is the same code under a `BENCHMARK_` prefix.
 *
 * The conversion was open-coded at every call site as
 * `code.startsWith('BENCHMARK_') ? code : 'BENCHMARK_' + code`. That is easy to
 * get subtly wrong in one place, and the places have to agree: the project card
 * and the flow editor both claim to name "the latest backtest of this
 * workflow", so if their idea of the benchmark code ever diverged they would
 * quietly point at different runs.
 */
export const BENCHMARK_PREFIX = 'BENCHMARK_';

/** The code a workflow's backtest runs are recorded under. Idempotent. */
export function benchmarkFlowCode(baseCode: string): string {
    if (!baseCode) return '';
    return baseCode.startsWith(BENCHMARK_PREFIX) ? baseCode : `${BENCHMARK_PREFIX}${baseCode}`;
}

/** The workflow a backtest run belongs to. Idempotent. */
export function baseFlowCode(runFlowCode: string): string {
    if (!runFlowCode) return '';
    return runFlowCode.startsWith(BENCHMARK_PREFIX)
        ? runFlowCode.slice(BENCHMARK_PREFIX.length)
        : runFlowCode;
}

/**
 * The code a workflow's supplied-sample runs are submitted under: the same
 * workflow, prefixed `VALIDATE_`, scoring alerts the user provides rather than
 * the institution's mined history. Idempotent.
 */
export const VALIDATE_PREFIX = 'VALIDATE_';

export function validateFlowCode(baseCode: string): string {
    if (!baseCode) return '';
    return baseCode.startsWith(VALIDATE_PREFIX) ? baseCode : `${VALIDATE_PREFIX}${baseCode}`;
}

/**
 * Publishing a flow copies it under `<code>_YYYYMMDDTHHMMSS`, and a run records
 * whichever copy ran. The family is the code with that stamp removed — the same
 * rule the API and the orchestration service apply — so every published build
 * of a flow is treated as the same flow.
 */
const PUBLISH_STAMP = /_\d{8}T\d{6}$/;

export function flowFamily(code: string): string {
    if (!code) return '';
    return code.replace(PUBLISH_STAMP, '');
}

/** The family a workflow's stock backtests are recorded under. */
export function benchmarkFamily(baseCode: string): string {
    return flowFamily(benchmarkFlowCode(flowFamily(baseCode)));
}

/**
 * Every family a workflow's supplied-sample runs may be recorded under.
 *
 * The first supplied runs were recorded as `BENCHMARK_<flow>_FROM_SAMPLE`
 * before the flow was renamed `VALIDATE_<flow>`; both are asked for so those
 * runs stay reachable until retention removes them.
 */
export function suppliedSampleFamilies(baseCode: string): string[] {
    if (!baseCode) return [];
    const base = flowFamily(baseCode);
    return [validateFlowCode(base), `${benchmarkFlowCode(base)}_FROM_SAMPLE`];
}

/**
 * The workflow any benchmark run belongs to, whichever flow recorded it: the
 * stock backtest, a supplied sample under either of its names, or a published
 * build of any of them.
 */
export function workflowCode(runFlowCode: string): string {
    if (!runFlowCode) return '';
    let code = flowFamily(runFlowCode);
    if (code.startsWith(VALIDATE_PREFIX)) return code.slice(VALIDATE_PREFIX.length);
    code = baseFlowCode(code);
    return code.endsWith('_FROM_SAMPLE') ? code.slice(0, -'_FROM_SAMPLE'.length) : code;
}
